/* global __RESIDESK_SENTRY_RELEASE__ */
import React from 'react'
import * as Sentry from '@sentry/react'

const dsn = import.meta.env.VITE_SENTRY_DSN
const buildRelease =
  typeof __RESIDESK_SENTRY_RELEASE__ !== 'undefined' ? __RESIDESK_SENTRY_RELEASE__ : ''
const release =
  import.meta.env.VITE_SENTRY_RELEASE ||
  import.meta.env.VITE_SOURCE_VERSION ||
  buildRelease ||
  undefined
const sentryLogsEnabled = import.meta.env.VITE_SENTRY_ENABLE_LOGS === 'true'

Sentry.init({
  dsn,
  enabled: Boolean(dsn),
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
  release,
  enableLogs: sentryLogsEnabled,
  beforeSend(event) {
    event.tags = {
      ...event.tags,
      hostname: window.location.hostname,
    }
    return event
  },
})

export const reportError = (error, context = {}) => {
  Sentry.withScope((scope) => {
    const {
      context: errorContext,
      domain,
      surface,
      route,
      integration,
      accountId,
      portfolioId,
      ...metadata
    } = context

    if (errorContext) scope.setTag('error.context', errorContext)
    if (domain) scope.setTag('domain', domain)
    if (surface) scope.setTag('surface', surface)
    if (route) scope.setTag('route', route)
    if (integration) scope.setTag('integration', integration)
    if (accountId) scope.setTag('accountId', accountId)
    if (portfolioId) scope.setTag('portfolioId', portfolioId)
    scope.setTag('runtime', 'browser')

    scope.setContext('residesk', {
      runtime: 'browser',
      context: errorContext,
      domain,
      surface,
      route,
      integration,
      accountId,
      portfolioId,
    })
    scope.setContext('errorMetadata', metadata)

    Sentry.captureException(error)
  })
}

export const setUser = (id, userInfo = {}) => {
  Sentry.setUser({
    id,
    email: userInfo?.email,
    username: userInfo?.name,
  })
  Sentry.setContext('userMetadata', userInfo)
}

export const clearUser = () => {
  Sentry.setUser(null)
  Sentry.setContext('userMetadata', null)
}

export const addBreadcrumb = (type, metadata = {}) => {
  Sentry.addBreadcrumb({
    category: type,
    data: metadata,
    level: 'info',
  })
}

export const setContext = (category, data = {}) => {
  Sentry.setContext(category, data)
}

export const logSentry = (level, message, attributes = {}) => {
  if (!sentryLogsEnabled || !message) {
    return
  }

  if (typeof Sentry.logger?.[level] === 'function') {
    Sentry.logger[level](message, attributes)
  }
}

export const sentryClient = {
  notify: reportError,
}

export const SentryErrorBoundary = ({ FallbackComponent, children }) =>
  React.createElement(
    Sentry.ErrorBoundary,
    {
      fallback: ({ error, resetError }) =>
        FallbackComponent ? React.createElement(FallbackComponent, { error, resetError }) : null,
      beforeCapture: (scope) => {
        scope.setTag('surface', 'root_error_boundary')
        scope.setTag('runtime', 'browser')
      },
    },
    children,
  )
